import type {
  Prioridade,
  StatusSolicitacao,
  TipoSolicitacao,
} from '@/features/solicitacoes/status';
import type { OrigemSolicitacao } from '@/features/solicitacoes/origem';

/**
 * Espelha as interfaces de `backend/src/relatorios/relatorios.service.ts`.
 * Qualquer campo novo precisa aparecer nos dois lados.
 */

/** Cabeçalho comum: todo bloco diz de que recorte está falando. */
export interface CabecalhoRelatorio {
  de: string;
  ate: string;
  dias: number;
  /** Quantas unidades entraram na conta. Nulo quando são todas. */
  unidades: number | null;
}

export interface RelatorioVolume {
  periodo: CabecalhoRelatorio;
  total: number;
  porUbs: { ubsId: string; nome: string; total: number }[];
  porStatus: { status: StatusSolicitacao; total: number }[];
  porTipo: { tipo: TipoSolicitacao; total: number }[];
  porPrioridade: { prioridade: Prioridade; total: number }[];
  /** Quanto do volume veio do estoque anterior ao KAVI. */
  porOrigem: { origem: OrigemSolicitacao; total: number }[];
  serie: { granularidade: 'dia' | 'mes'; pontos: { periodo: string; total: number }[] };
}

export interface LinhaQualidadeUbs {
  ubsId: string;
  nome: string;
  total: number;
  comPendencia: number;
  devolvidas: number;
  reabertas: number;
  taxaPendencia: number;
  taxaDevolucao: number;
}

export interface RelatorioQualidade {
  periodo: CabecalhoRelatorio;
  totais: { solicitacoes: number; comPendencia: number; devolvidas: number; reabertas: number };
  porUbs: LinhaQualidadeUbs[];
  motivosDevolucao: { motivo: string; total: number }[];
  pendenciasFrequentes: { descricao: string; total: number }[];
}

export interface RelatorioDemanda {
  periodo: CabecalhoRelatorio;
  porEspecialidade: { especialidadeId: string; nome: string; total: number }[];
  porProcedimento: { procedimento: string; total: number }[];
  procedimentoPorUbs: {
    ubsId: string;
    nome: string;
    itens: { procedimento: string; total: number }[];
  }[];
  especialidadesComAtrito: {
    especialidadeId: string;
    nome: string;
    total: number;
    problemas: number;
    taxaProblema: number;
  }[];
}

/** Horas médias e medianas de cada etapa. `null` quando não houve amostra. */
export interface EtapasTempo {
  amostra: number;
  ateLeitura: { media: number | null; mediana: number | null };
  ateDecisao: { media: number | null; mediana: number | null };
  ateAgendamento: { media: number | null; mediana: number | null };
}

export interface RelatorioTempos {
  periodo: CabecalhoRelatorio;
  geral: EtapasTempo;
  porUbs: (EtapasTempo & { ubsId: string; nome: string })[];
  porRegulador: (EtapasTempo & { reguladorId: string; nome: string })[];
  agendamentos: { origem: 'ESUS' | 'MANUAL'; total: number }[];
}

/** Blocos da tela, na ordem em que aparecem nas abas. */
export const BLOCOS = ['volume', 'qualidade', 'demanda', 'tempos'] as const;

export type Bloco = (typeof BLOCOS)[number];

export const ROTULOS_BLOCO: Record<Bloco, string> = {
  volume: 'Volume e fluxo',
  qualidade: 'Qualidade e gargalos',
  demanda: 'Demanda',
  tempos: 'Tempos',
};

export const DESCRICOES_BLOCO: Record<Bloco, string> = {
  volume: 'Quantas solicitações, de onde vêm e como evoluem no tempo.',
  qualidade: 'Onde o processo trava: pendência, devolução e reabertura.',
  demanda: 'O que mais se pede, no geral e por unidade.',
  tempos: 'Quanto tempo cada etapa leva, por unidade e por regulador.',
};

export function ehBloco(valor: string | null): valor is Bloco {
  return BLOCOS.includes(valor as Bloco);
}
